import { useEffect, useRef } from "react";

/**
 * Cloudflare Turnstile widget — JS port of app/src/components/common/TurnstileWidget.tsx.
 *
 * Loads the Cloudflare script once per page, renders a non-interactive
 * challenge, reports the solved token via `onToken`. On expire/error the
 * parent is told to clear the token so the form can't submit a stale value.
 *
 * If `siteKey` is empty (env var unset), the widget renders nothing — the
 * BE middleware is also a no-op in that case, so the form fails open.
 *
 * F-RATE-07. See SECURITY_AUDIT/02_findings.md.
 */

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";

let scriptPromise = null;

function loadTurnstileScript() {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener(
        "error",
        () => reject(new Error("Turnstile script failed to load")),
        { once: true },
      );
      return;
    }
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener("load", () => resolve(), { once: true });
    script.addEventListener(
      "error",
      () => reject(new Error("Turnstile script failed to load")),
      { once: true },
    );
    document.head.appendChild(script);
  });

  return scriptPromise;
}

const TurnstileWidget = ({ siteKey, onToken }) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    if (!siteKey) return undefined;
    let cancelled = false;

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token) => onToken(token),
          "error-callback": () => onToken(null),
          "expired-callback": () => onToken(null),
          "timeout-callback": () => onToken(null),
          appearance: "interaction-only",
        });
      })
      .catch(() => {
        // Script blocked/failed. Allow submit anyway — BE limiters still
        // protect us. Better than blocking real users on a CF outage.
        onToken("cf-script-load-failed");
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // ignore — widget may already be gone
        }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, onToken]);

  if (!siteKey) return null;
  return <div ref={containerRef} />;
};

export default TurnstileWidget;
